package com.dotdrives.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * docs/schema.md "Org settings" > `budgets`. Consolidates the legacy Goal/TotalBudget/
 * BudgetPeriod/StewardBudget/PipelinePhaseTemplateBudget tables: "an amount, over a period,
 * scoped to org or steward." `scopeRef` is null for `scope == "org"`, a
 * `drive_phase_templates.id` for `"phase_template"`, or an `org_members.user_id` for
 * `"steward"` — validated by a database trigger because Postgres cannot express a conditional FK target.
 */
@Serializable
data class Budget(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val scope: String,
    @SerialName("scope_ref")
    val scopeRef: String?,
    val amount: Double,
    @SerialName("period_start")
    val periodStart: String,
    @SerialName("period_end")
    val periodEnd: String,
    @SerialName("created_at")
    val createdAt: Instant,
    val archived: Boolean = false,
)

object BudgetScope {
    const val ORG = "org"
    const val PHASE_TEMPLATE = "phase_template"
    const val STEWARD = "steward"
}

/**
 * A new budget the client may submit. Only `org` and `steward` scopes are offered from this
 * module's UI — `phase_template` scope_ref would need a `drive_phase_templates.id` picker,
 * which lives in the Drives module and isn't wired here (see SettingsPortal.kt).
 */
data class NewBudget(
    val scope: String,
    val scopeRef: String?,
    val amount: Double,
    val periodStart: String,
    val periodEnd: String,
)

/**
 * docs/schema.md "Org settings" > `org_integrations`. Non-secret connection-status flags only
 * — the real OAuth tokens/Connect account IDs live in `private.qbo_connections` and
 * `private.stripe_connect_accounts`, never here.
 */
@Serializable
data class OrgIntegrations(
    @SerialName("org_id")
    val orgId: String,
    @SerialName("quickbooks_connected")
    val quickbooksConnected: Boolean,
    @SerialName("quickbooks_connected_at")
    val quickbooksConnectedAt: Instant?,
    @SerialName("quickbooks_display_name")
    val quickbooksDisplayName: String?,
    @SerialName("stripe_connected")
    val stripeConnected: Boolean,
    @SerialName("stripe_connected_at")
    val stripeConnectedAt: Instant?,
)

/**
 * docs/schema.md "Subscription and billing" > `subscriptions`. Read-only from every client:
 * no insert/update policy or grant exists for any role, by design — every row is written by a
 * Stripe webhook running as the service role (ADR 0005). Gated on
 * `has_perm_strict(org_id,'billing.read')`, so `field` gets an RLS-empty result, not this row —
 * [SettingsRepository.subscription] models that as `Result.success(null)`, not an error.
 */
@Serializable
data class Subscription(
    @SerialName("org_id")
    val orgId: String,
    val plan: String,
    val status: String,
    @SerialName("trial_ends_at")
    val trialEndsAt: Instant?,
    @SerialName("current_period_end")
    val currentPeriodEnd: Instant?,
    @SerialName("seats_limit")
    val seatsLimit: Int?,
    @SerialName("givers_limit")
    val giversLimit: Int?,
    @SerialName("created_at")
    val createdAt: Instant,
    @SerialName("updated_at")
    val updatedAt: Instant,
)

/**
 * The narrow surface `public.billing_status(p_org)` returns — `{status, trial_ends_at}` only,
 * reachable by every role including `field`, for the lapsed-trial/payment-failed banner. Never
 * read `subscriptions` itself for the banner; that table is `has_perm_strict`-gated and empty
 * for `field`.
 */
@Serializable
data class BillingStatus(
    val status: String?,
    @SerialName("trial_ends_at")
    val trialEndsAt: Instant?,
)

/** docs/schema.md "Org settings" — org_invites, a pending-invite row. */
data class OrgInvite(
    val id: String,
    val orgId: String,
    val email: String,
    val role: String,
    val createdBy: String,
    val createdAt: Instant,
    val expiresAt: Instant? = null,
    val acceptedAt: Instant? = null,
    val revokedAt: Instant? = null,
    val acceptedBy: String? = null,
)

/**
 * The single source of truth for which roles a client may assign via invite. Matches
 * `org_invites.role`'s check constraint exactly (foundation.sql: admin/member/field) — `owner`
 * is assigned once by `create_org`, never invited, and "Read-only" has no home in
 * `owner|admin|member|field` (docs/schema.md's open questions) so it is never added here.
 * Every invite-role UI and every controller-side role validation must derive from this list
 * directly, not maintain a parallel literal — see `SettingsController.setInviteRole` and
 * `InviteUserDialog`'s role picker (`webApp/.../pages/settings/`), both of which import this.
 */
val INVITABLE_ROLES: List<String> = listOf("admin", "member", "field")

/**
 * docs/schema.md "Tags" — an org-scoped catalog for autocomplete, shipped in the same
 * `20260902010000_settings_billing.sql` migration and the same schema section as `budgets`,
 * `org_integrations`, and `subscriptions`. `usage_count` is trigger-maintained
 * (`sync_tag_usage_count`), never client-written. Direct updates/deletes remain unavailable; tenant-checked RPCs rename/recolor
 * and archive tags while preserving giver and drive assignments.
 */
@Serializable
data class Tag(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val name: String,
    val color: String,
    @SerialName("usage_count")
    val usageCount: Int,
    val archived: Boolean = false,
    @SerialName("created_at")
    val createdAt: Instant,
)

data class NewTag(
    val name: String,
    val color: String,
)

data class ConfigurationSnapshot(val targets: List<Target>, val templates: List<DrivePhaseTemplate>, val phases: List<DrivePhase>)
